import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, FieldValues } from 'react-hook-form';
import { FaMoneyBill } from 'react-icons/fa';
import { MdKeyboardBackspace } from 'react-icons/md';
import { OptionTypeBase } from 'react-select';
import axios from 'axios';
import FileDownload from 'js-file-download';

import { onDelay } from 'utils/debounce';

import { IOptionReactSelect } from 'shared/interfaces';

import { getClientes } from 'services/api/cliente';
import { getFormasPagamento } from 'services/api/formaPagamento';
import { getContas } from 'services/api/contas';
import { getRelatorioBaixas } from 'services/api/contasReceber';

import { useToast } from 'contexts';
import * as C from 'components';

import * as S from './styles';

import { IRelatorioContasReceberBaixasForm } from '../interfaces';

export const ContasReceberBaixasPrint: React.FC = () => {
  const { addToast } = useToast();

  const currentDate = new Date();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
  } = useForm<FieldValues>({
    defaultValues: {
      data_pagamento_inicial: new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1,
      ),
      data_pagamento_final: new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0,
      ),
    },
  });

  const [loading, setLoading] = useState(false);

  const loadOptionsFormas = onDelay(
    (
      inputValue: string,
      callback: (options: OptionTypeBase[]) => void,
    ): void => {
      getFormasPagamento({
        page: 1,
        per_page: 15,
        term: inputValue,
      }).then((response) => {
        const values: IOptionReactSelect[] = response.data.map((item) => {
          return {
            label: item.nome,
            value: item.id,
          };
        });

        return callback(values);
      });
    },
    500,
  );

  const loadOptionsClientes = onDelay(
    (
      inputValue: string,
      callback: (options: OptionTypeBase[]) => void,
    ): void => {
      getClientes({
        page: 1,
        per_page: 15,
        term: inputValue,
      }).then((response) => {
        const values: IOptionReactSelect[] = response.data.map((item) => {
          return {
            label: item.nome,
            value: item.id,
          };
        });

        return callback(values);
      });
    },
    500,
  );

  const loadOptionsContas = onDelay(
    (
      inputValue: string,
      callback: (options: OptionTypeBase[]) => void,
    ): void => {
      getContas({
        page: 1,
        per_page: 15,
        term: inputValue,
      }).then((response) => {
        const values: IOptionReactSelect[] = response.data.map((item) => {
          return {
            label: item.nome,
            value: item.id,
          };
        });

        return callback(values);
      });
    },
    500,
  );

  const onSubmit = useCallback(
    async (data: IRelatorioContasReceberBaixasForm) => {
      try {
        setLoading(true);

        const response = await getRelatorioBaixas(data);

        if (response.link) {
          axios({
            url: `${response.link}`,
            method: 'GET',
            responseType: 'blob',
          }).then((relatorio) => {
            FileDownload(relatorio.data, 'Recebimentos.pdf');
          });
        }

        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Salvo com sucesso',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Ops, Erro',
          description: 'Ocorreu um erro ao inserir um novo registro',
        });
      } finally {
        setLoading(false);
      }
    },

    [addToast],
  );

  return (
    <S.Container>
      <C.HeaderBackground>
        <S.HeaderPageDatail>
          <S.BackLink>
            <Link to="/baixas/contas-receber">
              <MdKeyboardBackspace size={24} color="#fff" />
              Recebimentos
            </Link>
          </S.BackLink>

          <C.HeaderInfo title="Relatorios recebimentos" icon={FaMoneyBill} />
        </S.HeaderPageDatail>
      </C.HeaderBackground>

      <S.Panel>
        <form onSubmit={handleSubmit(onSubmit)}>
          <S.FormContainer>
            <C.FormGroup>
              <C.Label>Data pagamento inicial</C.Label>
              <C.InputDatePicker
                {...register('data_pagamento_inicial')}
                control={control}
                errors={errors.data_pagamento_inicial}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_pagamento_inicial')}
                name="data_pagamento_inicial"
                placeholder="Data pagamento"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Data pagamento final</C.Label>
              <C.InputDatePicker
                {...register('data_pagamento_final')}
                control={control}
                errors={errors.data_pagamento_final}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_pagamento_final')}
                name="data_pagamento_final"
                placeholder="Data pagamento"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Data lançamento inicial</C.Label>
              <C.InputDatePicker
                {...register('data_lancamento_inicial')}
                control={control}
                errors={errors.data_lancamento_inicial}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_lancamento_inicial')}
                name="data_lancamento_inicial"
                placeholder="Data lancamento"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Data lançamento final</C.Label>
              <C.InputDatePicker
                {...register('data_lancamento_final')}
                control={control}
                errors={errors.data_lancamento_final}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_lancamento_final')}
                name="data_lancamento_final"
                placeholder="Data lancamento"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Cliente</C.Label>
              <C.ReactSelectAsync
                {...register('cliente_id')}
                control={control}
                errors={errors.cliente_id}
                loadOptions={loadOptionsClientes}
                isClearable
                isSearchable
                loadingMessage="Buscando..."
                noOptionsMessage="Nenhum cliente localizado"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Forma</C.Label>
              <C.ReactSelectAsync
                {...register('forma_pagamento_id')}
                control={control}
                errors={errors.forma_pagamento_id}
                loadOptions={loadOptionsFormas}
                isClearable
                isSearchable
                loadingMessage="Buscando..."
                noOptionsMessage="Nenhuma forma pagamento localizada"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Conta</C.Label>
              <C.ReactSelectAsync
                {...register('conta_id')}
                control={control}
                errors={errors.conta_id}
                loadOptions={loadOptionsContas}
                isClearable
                isSearchable
                loadingMessage="Buscando..."
                noOptionsMessage="Nenhuma conta localizada"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Observação</C.Label>
              <C.Input
                {...register('observacao')}
                errors={errors.observacao}
                placeholder="Observação"
              />
            </C.FormGroup>
          </S.FormContainer>

          <C.Button
            variant="primary"
            disabled={loading}
            loading={loading}
            type="submit"
            title="Gerar relatorio"
          >
            Gerar relatório
          </C.Button>
        </form>
      </S.Panel>
    </S.Container>
  );
};
